import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnChanges, OnDestroy, OnInit, Output, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { CdkDrag, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';
import { TranslateService } from '@app/core/services/translate.service';
import { GanttColumn, GanttToolbarOptions } from '../gantt.types';
import { createDefaultGanttToolbarOptions, customCoerceBooleanProperty } from '../gantt-adapter';
import { getColumnPropertyKey } from '../gantt-remember';

@Component({
    selector: 'tt-gantt-toolbar',
    standalone: false,
    templateUrl: './gantt-toolbar.component.html',
    styleUrls: ['./gantt-toolbar.component.css'],
})
export class GanttToolbarComponent implements OnInit, OnChanges, OnDestroy {
    @Input() public ttToolbar: GanttToolbarOptions = createDefaultGanttToolbarOptions();
    @Input() public ttCriticalPath = false;
    @Input() public ttResourceGraph = false;
    @Input() public ttFieldNames = false;
    @Input() public ttBackwardPlanning = false;
    @Input() public ttColumns: GanttColumn[] = [];
    @Input() public ttResourceColumns: GanttColumn[] = [];
    @Input() public ttLoading = false;
    @Input() public ttSaving = false;
    @Input() public ttAutoSave = true;
    @Input() public ttHasChanges = false;
    @Output() public ttColumnVisibility = new EventEmitter<{ propertyKey: string; visible: boolean }>();
    @Output() public ttColumnOrder = new EventEmitter<string[]>();
    @Output() public ttResetColumns = new EventEmitter<void>();
    @Output() public ttResourceColumnVisibility = new EventEmitter<{ propertyKey: string; visible: boolean }>();
    @Output() public ttResourceColumnOrder = new EventEmitter<string[]>();
    @Output() public ttResetResourceColumns = new EventEmitter<void>();
    @Output() public ttReload = new EventEmitter<void>();
    @Output() public ttSave = new EventEmitter<void>();
    @Output() public ttCriticalPathChange = new EventEmitter<boolean>();
    @Output() public ttResourceGraphChange = new EventEmitter<boolean>();
    @Output() public ttFieldNamesChange = new EventEmitter<boolean>();
    @Output() public ttBackwardPlanningChange = new EventEmitter<boolean>();
    @ViewChild('columnsTemplate', { static: true }) private columnsTemplate!: TemplateRef<unknown>;

    public readonly columnCheckboxPrefix = 'gantt-column-' + crypto.randomUUID() + '-';
    public columnSearch = '';
    public columnArea: 'tasks' | 'resources' = 'tasks';
    public labels = {
        moveUp: 'Move up', moveDown: 'Move down', reorder: 'Reorder',
        chooseColumns: 'Choose Columns', search: 'Search…', selectAll: 'Select all',
        close: 'Close', reset: 'Reset', noColumns: 'No columns found',
        forwardPlanning: 'Forward planning', backwardPlanning: 'Backward planning',
        tasks: 'Tasks', resources: 'Resources',
    };
    private readonly labelKeys: Record<keyof typeof this.labels, string> = {
        moveUp: 'move_up', moveDown: 'move_down', reorder: 'reorder',
        chooseColumns: 'choose_columns', search: 'search', selectAll: 'select_all',
        close: 'close', reset: 'reset', noColumns: 'no_columns_found',
        forwardPlanning: 'forward_planning', backwardPlanning: 'backward_planning',
        tasks: 'tasks', resources: 'resources',
    };
    private columnsOverlay?: OverlayRef;
    private columnsEvents = new Subscription();
    private columnTrigger?: HTMLElement;
    private columnDrag?: CdkDrag;

    constructor(
        private translate: TranslateService,
        private overlay: Overlay,
        private viewContainer: ViewContainerRef,
        private host: ElementRef<HTMLElement>,
        @Inject(DOCUMENT) private document: Document,
    ) {}

    public async ngOnInit(): Promise<void> {
        const keys = Object.keys(this.labels) as Array<keyof typeof this.labels>;
        await Promise.all(keys.map(async key => {
            const id = this.labelKeys[key];
            const translated = await this.translate.translate(id);
            if (typeof translated === 'string' && translated.trim() && translated !== id) this.labels[key] = translated;
        }));
    }

    public ngOnChanges(): void {
        if (!this.ttResourceColumns.length) this.columnArea = 'tasks';
        if (!this.ttToolbar.enabled || !this.ttToolbar.columns) this.closeColumns();
    }

    public ngOnDestroy(): void { this.closeColumns(false); }
    public get columnsOpen(): boolean { return this.columnsOverlay?.hasAttached() ?? false; }
    public get busy(): boolean { return this.ttLoading || this.ttSaving; }
    public get activeColumns(): GanttColumn[] { return this.columnArea === 'resources' ? this.ttResourceColumns : this.ttColumns; }
    public selectColumnArea(area: 'tasks' | 'resources'): void { this.columnArea = area; this.columnSearch = ''; }
    public resetColumns(): void { (this.columnArea === 'resources' ? this.ttResetResourceColumns : this.ttResetColumns).emit(); }
    public get filteredColumns(): GanttColumn[] {
        const search = this.columnSearch.trim().toLocaleLowerCase();
        return search ? this.activeColumns.filter(column => `${column.label ?? ''} ${getColumnPropertyKey(column)}`.toLocaleLowerCase().includes(search)) : this.activeColumns;
    }
    public get allFilteredVisible(): boolean {
        const columns = this.filteredColumns;
        return columns.length > 0 && columns.every(column => this.isVisible(column));
    }
    public get someFilteredVisible(): boolean {
        const columns = this.filteredColumns;
        return columns.some(column => this.isVisible(column)) && !columns.every(column => this.isVisible(column));
    }

    public toggleColumns(event: MouseEvent): void {
        if (this.columnsOpen) { this.closeColumns(); return; }
        if (this.busy || !this.ttToolbar.enabled || !this.ttToolbar.columns) return;
        this.columnSearch = '';
        this.columnTrigger = event.currentTarget instanceof HTMLElement ? event.currentTarget : this.host.nativeElement.querySelector<HTMLButtonElement>('[data-action="columns"] button') ?? undefined;
        this.columnsOverlay = this.overlay.create({
            width: '300px',
            maxWidth: 'calc(100vw - 24px)',
            maxHeight: 'calc(100vh - 24px)',
            positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
            scrollStrategy: this.overlay.scrollStrategies.noop(),
            hasBackdrop: false,
        });
        this.inheritPanelTheme(this.columnsOverlay.overlayElement);
        this.columnsOverlay.attach(new TemplatePortal(this.columnsTemplate, this.viewContainer));
        this.columnsEvents = new Subscription();
        this.columnsEvents.add(this.columnsOverlay.keydownEvents().subscribe(key => {
            if (key.key === 'Escape') {
                key.preventDefault();
                key.stopPropagation();
                this.closeColumns();
            }
        }));
        this.columnsEvents.add(this.columnsOverlay.detachments().subscribe(() => this.closeColumns(false)));
        this.columnTrigger?.setAttribute('aria-expanded', 'true');
        this.columnTrigger?.setAttribute('aria-controls', this.columnCheckboxPrefix + 'panel');
        this.columnsOverlay.overlayElement.querySelector<HTMLInputElement>('input[type="search"]')?.focus();
    }

    public closeColumns(restoreFocus = true): void {
        const overlay = this.columnsOverlay;
        if (!overlay) return;
        const focused = this.document.activeElement;
        const restore = restoreFocus && (focused === this.document.body || (focused !== null && overlay.overlayElement.contains(focused)));
        this.columnsOverlay = undefined;
        this.columnDrag = undefined;
        this.columnsEvents.unsubscribe();
        overlay.dispose();
        this.columnTrigger?.setAttribute('aria-expanded', 'false');
        this.columnTrigger?.removeAttribute('aria-controls');
        if (restore && this.columnTrigger?.isConnected) this.columnTrigger.focus();
    }

    @HostListener('window:resize')
    public constrainColumns(drag = this.columnDrag): void {
        const window = this.document.defaultView;
        if (!this.columnsOverlay || !drag || !window) return;
        this.columnDrag = drag;
        const rect = this.columnsOverlay.overlayElement.getBoundingClientRect();
        const position = drag.getFreeDragPosition();
        const x = Math.max(12 - rect.left, Math.min(0, window.innerWidth - 12 - rect.right));
        const y = Math.max(12 - rect.top, Math.min(0, window.innerHeight - 12 - rect.bottom));
        drag.setFreeDragPosition({ x: position.x + x, y: position.y + y });
    }

    private inheritPanelTheme(panel: HTMLElement): void {
        const styles = this.document.defaultView?.getComputedStyle(this.host.nativeElement);
        if (!styles) return;
        for (const property of ['--dhx-gantt-base-colors-background', '--dhx-gantt-base-colors-background-alt', '--dhx-gantt-base-colors-border',
            '--tt-body-text-color', '--tt-body-text-color-rgb']) {
            const value = styles.getPropertyValue(property);
            if (value) panel.style.setProperty(property, value);
        }
        panel.style.fontFamily = styles.fontFamily;
        panel.style.color = styles.color;
    }

    public columnLabel(column: GanttColumn): string {
        const propertyKey = getColumnPropertyKey(column);
        return this.ttFieldNames ? propertyKey : column.label || propertyKey;
    }
    public isVisible(column: GanttColumn): boolean { return !customCoerceBooleanProperty(column.hidden ?? column.hide ?? false); }
    public key(_index: number, column: GanttColumn): string { return getColumnPropertyKey(column); }
    public isLastVisible(column: GanttColumn): boolean {
        return this.isVisible(column) && this.activeColumns.filter(item => this.isVisible(item)).length === 1;
    }
    public toggle(column: GanttColumn, visible: boolean): void {
        if (this.busy || (!visible && this.isLastVisible(column))) return;
        (this.columnArea === 'resources' ? this.ttResourceColumnVisibility : this.ttColumnVisibility).emit({ propertyKey: getColumnPropertyKey(column), visible });
    }
    public toggleAll(visible: boolean): void {
        if (this.busy) return;
        const filtered = this.filteredColumns;
        const keys = new Set(filtered.map(column => getColumnPropertyKey(column)));
        const outsideVisible = this.activeColumns.some(column => this.isVisible(column) && !keys.has(getColumnPropertyKey(column)));
        const keep = !visible && !outsideVisible ? this.activeColumns.find(column => this.isVisible(column)) : undefined;
        for (const column of filtered) {
            if (column !== keep && this.isVisible(column) !== visible) this.toggle(column, visible);
        }
    }
    public drop(event: CdkDragDrop<GanttColumn[]>): void { this.moveFiltered(event.previousIndex, event.currentIndex); }
    public moveFiltered(from: number, to: number): void {
        const columns = this.filteredColumns;
        if (!columns[from] || !columns[to]) return;
        this.move(this.activeColumns.indexOf(columns[from]), this.activeColumns.indexOf(columns[to]));
    }
    public move(from: number, to: number): void {
        if (this.busy || from === to || from < 0 || to < 0 || from >= this.activeColumns.length || to >= this.activeColumns.length) return;
        const ordered = [...this.activeColumns];
        moveItemInArray(ordered, from, to);
        (this.columnArea === 'resources' ? this.ttResourceColumnOrder : this.ttColumnOrder).emit(ordered.map(getColumnPropertyKey));
    }
}
